import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { langFromPath, switchLangPath } from '../i18n/routes';

const SITE_URL = 'https://shiny-cleaning.nl';
const DEFAULT_TITLE = 'Shiny-Cleaning — Premium Auto Detailing Deventer';
const DEFAULT_DESCRIPTION =
  'Premium auto detailing in Deventer. Lakcorrectie, keramische coating, PPF, interieur reiniging en detailing aan huis. Vraag vrijblijvend een offerte aan.';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const normalize = (p) => {
  if (!p || p === '/') return '';
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
};

/**
 * SEO — per-page meta tags, Open Graph, Twitter Cards, hreflang and JSON-LD.
 * Canonical and hreflang are derived from the real current URL (NL or /en).
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  keywords,
  jsonLd,
  noindex = false,
  appendSite = true,
}) => {
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);

  const finalTitle = title
    ? appendSite
      ? `${title} | Shiny-Cleaning`
      : title
    : DEFAULT_TITLE;

  const nlPath = normalize(lang === 'nl' ? pathname : switchLangPath(pathname, 'nl'));
  const enPath = normalize(lang === 'en' ? pathname : switchLangPath(pathname, 'en'));
  const canonical = `${SITE_URL}${normalize(pathname)}`;

  const jsonLdItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{finalTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang — NL default + EN alternate */}
      <link rel="alternate" hrefLang="nl-NL" href={`${SITE_URL}${nlPath}`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}${enPath}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${nlPath}`} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Shiny-Cleaning" />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === 'en' ? 'en_GB' : 'nl_NL'} />
      {lang === 'en' && <meta property="og:locale:alternate" content="nl_NL" />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLdItems.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
